interface GoogleTokens {
  access_token: string;
  refresh_token?: string;
  expires_in: number;
  token_type: string;
  scope: string;
  expires_at?: number;
}

interface GoogleUserInfo {
  id: string;
  email: string;
  verified_email: boolean;
  name: string;
  given_name: string;
  family_name: string;
  picture: string;
  locale: string;
}

class GoogleAuthManager {
  private tokens: GoogleTokens | null = null;

  storeTokens(tokens: GoogleTokens) {
    const expiresAt = Date.now() + tokens.expires_in * 1000;
    this.tokens = { ...tokens, expires_at: expiresAt };

    if (typeof window !== 'undefined') {
      localStorage.setItem('google_tokens', JSON.stringify(this.tokens));
    }
  }

  getStoredTokens(): GoogleTokens | null {
    if (typeof window === 'undefined') return null;

    const stored = localStorage.getItem('google_tokens');
    if (!stored) return null;

    try {
      this.tokens = JSON.parse(stored);
      return this.tokens;
    } catch {
      return null;
    }
  }

  async getValidAccessToken(): Promise<string | null> {
    let tokens = this.tokens || this.getStoredTokens();
    if (!tokens) return null;

    // Check if token is expired (with 5 minute buffer)
    const isExpired = tokens.expires_at && Date.now() > tokens.expires_at - 300000;

    if (isExpired && tokens.refresh_token) {
      try {
        tokens = await this.refreshAccessToken(tokens.refresh_token);
        if (tokens) {
          this.storeTokens(tokens);
        }
      } catch (error) {
        console.error('Failed to refresh token:', error);
        this.clearTokens();
        return null;
      }
    }

    return tokens?.access_token || null;
  }

  private async refreshAccessToken(refreshToken: string): Promise<GoogleTokens | null> {
    try {
      const response = await fetch('/api/auth/google/refresh', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ refresh_token: refreshToken })
      });

      if (!response.ok) {
        throw new Error('Failed to refresh token');
      }

      const data = await response.json();
      return data.tokens;
    } catch (error) {
      console.error('Token refresh error:', error);
      return null;
    }
  }

  async getUserInfo(accessToken?: string): Promise<GoogleUserInfo | null> {
    const token = accessToken || (await this.getValidAccessToken());
    if (!token) return null;

    try {
      const response = await fetch(
        `https://www.googleapis.com/oauth2/v2/userinfo?access_token=${token}`
      );
      if (!response.ok) {
        throw new Error('Failed to get user info');
      }
      return await response.json();
    } catch (error) {
      console.error('Error getting user info:', error);
      return null;
    }
  }

  clearTokens() {
    this.tokens = null;
    if (typeof window !== 'undefined') {
      localStorage.removeItem('google_tokens');
      localStorage.removeItem('google_access_token'); // Clean up old storage
    }
  }
}

export const googleAuth = new GoogleAuthManager();
